<script setup>
// Prettier owns closing-tag wrapping; the legacy Vue rule produces conflicting fixes.
/* eslint-disable vue/html-closing-bracket-newline */
import { ref, computed, nextTick, onBeforeUnmount, watch } from 'vue';
import LeadScoringConfigAPI from 'dashboard/api/leadScoringConfig';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { translateChannelLabel } from 'dashboard/components/widgets/conversation/header/channelMeta';
import { toIntlLocale } from 'shared/helpers/intlLocale';

const { t, locale } = useI18n();
const CRON_EXAMPLE = '0 2 * * *';
const CRON_REFERENCE = 'crontab.guru';
const { isWorkspaceV2 } = useWorkspaceV2();
const previewDialog = ref(null);
const { accountId } = useAccount();
let contextVersion = 0;
const captureContext = () => {
  const version = contextVersion;
  const account = accountId.value;
  return () => version === contextVersion && account === accountId.value;
};
onBeforeUnmount(() => {
  contextVersion += 1;
});

// Config do Lead Scoring — feature nativa (gated por lead_scoring flag) extraída
// do namespace VOIP. A IA textual e seu modelo são geridos pela plataforma;
// o admin configura horário, prompt livre e limite por execução.
const defaultConfig = () => ({
  enabled: false,
  cron_expression: '0 2 * * *',
  max_per_run: 500,
  changed_only: true,
  custom_prompt: '',
  default_prompt: '',
  last_run_at: null,
  // Ultima analise agendada e ultima manual, cada uma com quantos contatos
  // entraram no dia (LeadScoring::LastRunsSummary).
  last_runs: null,
  text_ai_ready: false,
  // Filtros de escopo (v79+): inbox_ids vazio = todas as inboxes.
  inbox_ids: [],
  ignore_whatsapp_groups: true,
});
const cfg = ref(defaultConfig());
const availableInboxes = ref([]);
const availableLabels = ref([]);
const availableFunnels = ref([]);
const previewSystemPrompt = ref('');
const showPreview = ref(false);
watch(showPreview, async visible => {
  if (!isWorkspaceV2.value) return;
  await nextTick();
  if (visible) previewDialog.value?.open();
  else previewDialog.value?.close();
});
const promptTextareaRef = ref(null);

const saving = ref(false);
const flash = ref(false);
const running = ref(false);
const runFlash = ref('');
const runFlashSuccess = ref(false);

// Hora em 2 selects (HH / MM) em vez de <input type="time"> — o type=time
// respeita o locale do SO do usuario, entao em maquinas com en-US cai em
// AM/PM mesmo com lang="pt-BR". Selects numericos sao imunes a isso.
const simpleHour = ref('02');
const simpleMinute = ref('00');
const advancedMode = ref(false);

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) =>
  String(i).padStart(2, '0')
);
// Preserve every valid minute accepted by the saved cron expression.
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) =>
  String(i).padStart(2, '0')
);

const parsedFromCron = computed(() => {
  const parts = (cfg.value.cron_expression || '').trim().split(/\s+/);
  const isSimple =
    parts.length === 5 &&
    /^\d{1,2}$/.test(parts[0]) &&
    /^\d{1,2}$/.test(parts[1]) &&
    Number(parts[0]) < 60 &&
    Number(parts[1]) < 24 &&
    parts[2] === '*' &&
    parts[3] === '*' &&
    parts[4] === '*';
  if (!isSimple) return null;
  return {
    hour: String(parts[1]).padStart(2, '0'),
    minute: String(parts[0]).padStart(2, '0'),
  };
});

function syncSimpleFromCron() {
  const parsed = parsedFromCron.value;
  if (parsed) {
    simpleHour.value = parsed.hour;
    simpleMinute.value = parsed.minute;
    advancedMode.value = false;
  } else {
    advancedMode.value = true;
  }
}

function syncCronFromSimple() {
  cfg.value.cron_expression = `${parseInt(simpleMinute.value, 10)} ${parseInt(simpleHour.value, 10)} * * *`;
}

watch([simpleHour, simpleMinute], () => {
  if (!advancedMode.value) syncCronFromSimple();
});

// Aplica payload vindo do backend (show ou update) no state local,
// separando available_inboxes (UI only) e normalizando inbox_ids.
// Chamado por loadConfig E save — evita drift de normalizacao.
function applyServerPayload(data) {
  if (!data) {
    // eslint-disable-next-line no-console
    console.warn('[LeadScoringSettings] applyServerPayload: data vazio/null');
    return;
  }
  availableInboxes.value = Array.isArray(data.available_inboxes)
    ? data.available_inboxes
    : availableInboxes.value;
  availableLabels.value = Array.isArray(data.available_labels)
    ? data.available_labels
    : availableLabels.value;
  availableFunnels.value = Array.isArray(data.available_funnels)
    ? data.available_funnels
    : availableFunnels.value;
  previewSystemPrompt.value =
    data.preview_system_prompt || previewSystemPrompt.value;

  const {
    available_inboxes: _ai,
    available_labels: _al,
    available_funnels: _af,
    preview_system_prompt: _pp,
    ...configFields
  } = data;

  // IMPORTANTE: Object.assign preserva a MESMA referencia do objeto cfg
  // (Vue reativo). Substituir via `cfg.value = { ... }` quebrava a
  // ligacao reativa do Switch e outros componentes em alguns browsers,
  // fazendo parte da UI sumir apos salvar/carregar.
  Object.assign(cfg.value, configFields);

  if (!cfg.value.custom_prompt) {
    cfg.value.custom_prompt = cfg.value.default_prompt || '';
  }
  cfg.value.inbox_ids = Array.isArray(cfg.value.inbox_ids)
    ? cfg.value.inbox_ids.map(Number).filter(Number.isFinite)
    : [];
  // Garante booleanos (backend pode retornar como true/false strings em
  // alguns paths esquisitos).
  cfg.value.enabled = Boolean(cfg.value.enabled);
  cfg.value.changed_only = Boolean(cfg.value.changed_only);
  cfg.value.ignore_whatsapp_groups = cfg.value.ignore_whatsapp_groups !== false;

  syncSimpleFromCron();
}

// Insere texto no cursor do textarea do prompt. Usado pelos chips de
// etiqueta e estagio — click insere o nome exato no ponto onde o admin
// estava digitando.
function insertIntoPrompt(snippet) {
  const ta = promptTextareaRef.value;
  const current = cfg.value.custom_prompt || '';
  if (!ta) {
    cfg.value.custom_prompt = current + snippet;
    return;
  }
  const start = ta.selectionStart ?? current.length;
  const end = ta.selectionEnd ?? current.length;
  cfg.value.custom_prompt =
    current.slice(0, start) + snippet + current.slice(end);
  // Reposiciona cursor apos o texto inserido no proximo tick.
  setTimeout(() => {
    ta.focus();
    const newPos = start + snippet.length;
    ta.setSelectionRange(newPos, newPos);
  }, 0);
}

const loadError = ref('');
const loading = ref(false);
const loaded = ref(false);

async function loadConfig() {
  if (loading.value) return;
  const isCurrent = captureContext();
  loading.value = true;
  loadError.value = '';
  try {
    const { data } = await LeadScoringConfigAPI.show();
    if (!isCurrent()) return;
    applyServerPayload(data);
    loaded.value = true;
  } catch (e) {
    if (!isCurrent()) return;
    loadError.value =
      e?.response?.data?.error ||
      e?.message ||
      t('WORKSPACE_V2.SCORING.LOAD_ERROR');
  } finally {
    if (isCurrent()) loading.value = false;
  }
}

function toggleInbox(inboxId) {
  const id = Number(inboxId);
  const idx = cfg.value.inbox_ids.indexOf(id);
  if (idx >= 0) {
    cfg.value.inbox_ids.splice(idx, 1);
  } else {
    cfg.value.inbox_ids.push(id);
  }
}

const allInboxesSelected = computed(() => cfg.value.inbox_ids.length === 0);
const inboxSummary = computed(() => {
  if (allInboxesSelected.value) return t('WORKSPACE_V2.SCORING.ALL_INBOXES');
  const n = cfg.value.inbox_ids.length;
  return t('WORKSPACE_V2.SCORING.SELECTED_INBOXES', { count: n }, n);
});

const saveError = ref('');
watch(
  accountId,
  () => {
    contextVersion += 1;
    loading.value = false;
    loaded.value = false;
    saving.value = false;
    running.value = false;
    flash.value = false;
    saveError.value = '';
    runFlash.value = '';
    showPreview.value = false;
    previewSystemPrompt.value = '';
    availableInboxes.value = [];
    availableLabels.value = [];
    availableFunnels.value = [];
    cfg.value = defaultConfig();
    loadConfig();
  },
  { immediate: true }
);

async function save() {
  if (saving.value || running.value || loading.value || !loaded.value) return;
  const isCurrent = captureContext();
  saving.value = true;
  flash.value = false;
  saveError.value = '';
  try {
    if (!advancedMode.value) syncCronFromSimple();
    const payload = {
      enabled: cfg.value.enabled,
      cron_expression: cfg.value.cron_expression,
      max_per_run: cfg.value.max_per_run,
      changed_only: cfg.value.changed_only,
      custom_prompt: cfg.value.custom_prompt,
      inbox_ids: cfg.value.inbox_ids || [],
      ignore_whatsapp_groups: cfg.value.ignore_whatsapp_groups,
    };
    const draft = JSON.stringify(cfg.value);
    const { data } = await LeadScoringConfigAPI.update(payload);
    if (!isCurrent()) return;
    const unchanged = JSON.stringify(cfg.value) === draft;
    if (unchanged) applyServerPayload(data);
    flash.value = unchanged;
    setTimeout(() => {
      if (isCurrent()) flash.value = false;
    }, 2000);
  } catch (e) {
    if (!isCurrent()) return;
    // Nao deixa erro silencioso quebrar a UI. Anteriormente o save sem
    // catch em caso de erro deixava o cfg em estado parcial e alguns
    // campos sumiam sem feedback.
    // eslint-disable-next-line no-console
    console.error('[LeadScoringSettings] save falhou', e);
    const msg =
      e?.response?.data?.errors || e?.response?.data?.error || e?.message;
    saveError.value = t('WORKSPACE_V2.SCORING.SAVE_ERROR', {
      error: Array.isArray(msg)
        ? msg.join('; ')
        : msg || t('WORKSPACE_V2.SCORING.RETRY'),
    });
    // Preserve the current draft so the user can retry the same changes.
  } finally {
    if (isCurrent()) saving.value = false;
  }
}

const nextRunDescription = computed(() => {
  if (!cfg.value.enabled) return t('WORKSPACE_V2.SCORING.INACTIVE');
  if (advancedMode.value) {
    return t('WORKSPACE_V2.SCORING.CRON_SCHEDULE', {
      expression: cfg.value.cron_expression,
    });
  }
  return t('WORKSPACE_V2.SCORING.DAILY_SCHEDULE', {
    time: `${simpleHour.value}:${simpleMinute.value}`,
  });
});

// "Ultima execucao" lia last_run_at, que tambem e gravado pelo Analisar agora:
// o clique aparecia como se a rotina tivesse rodado. As duas saem separadas.
const formatRunDate = iso => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(toIntlLocale(locale?.value), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const lastRunLines = computed(() =>
  ['automatic', 'manual'].map(kind => {
    const run = cfg.value.last_runs?.[kind];
    const prefix = kind === 'automatic' ? 'LAST_AUTOMATIC' : 'LAST_MANUAL';
    if (!run?.at) {
      return { kind, text: t(`WORKSPACE_V2.SCORING.${prefix}_NEVER`) };
    }
    return {
      kind,
      text: t(`WORKSPACE_V2.SCORING.${prefix}`, {
        date: formatRunDate(run.at),
        count: run.contacts ?? 0,
      }),
    };
  })
);

function resetToDefaultPrompt() {
  cfg.value.custom_prompt = cfg.value.default_prompt || '';
}

// Trigger manual do scoring — enfileira ScoreLeadJob pros contatos
// candidatos AGORA (respeita max_per_run e changed_only mas ignora
// config.enabled?). Processamento acontece em background no Sidekiq,
// entao o feedback aqui e so "enfileirado com sucesso".
async function runNow() {
  if (running.value || saving.value || loading.value || !loaded.value) return;
  const isCurrent = captureContext();
  if (!cfg.value.text_ai_ready) {
    runFlash.value = t('WORKSPACE_V2.SCORING.AI_UNAVAILABLE');
    setTimeout(() => {
      if (isCurrent()) runFlash.value = '';
    }, 3500);
    return;
  }
  running.value = true;
  runFlash.value = '';
  try {
    const { data } = await LeadScoringConfigAPI.runNow();
    if (!isCurrent()) return;
    const n = data?.enqueued ?? 0;
    runFlash.value =
      n === 0
        ? t('WORKSPACE_V2.SCORING.RUN_EMPTY')
        : t('WORKSPACE_V2.SCORING.RUN_QUEUED', { count: n });
    runFlashSuccess.value = n > 0;
  } catch (e) {
    if (!isCurrent()) return;
    runFlash.value = t('WORKSPACE_V2.SCORING.RUN_ERROR', {
      error: e?.response?.data?.error || e.message,
    });
  } finally {
    if (isCurrent()) running.value = false;
    setTimeout(() => {
      if (isCurrent()) runFlash.value = '';
    }, 6000);
  }
}
</script>

<template>
  <div
    class="w-full h-full overflow-y-auto bg-n-surface-1"
    data-testid="page-lead-scoring"
  >
    <div class="max-w-3xl mx-auto p-6 space-y-6 pb-16">
      <!-- O titulo vem do cabecalho da configuracao (leadScoring.routes.js):
           repetir aqui dava dois titulos de pagina. -->
      <p class="text-sm text-n-slate-11">
        {{ t('WORKSPACE_V2.SCORING.INTRO') }}
      </p>

      <div
        v-if="loadError"
        role="alert"
        class="border border-n-ruby-6 bg-n-ruby-3 text-n-ruby-11 rounded-2xl p-4 text-sm flex items-start gap-2"
      >
        <span class="i-ph-warning size-4 shrink-0 mt-0.5" />
        <div class="flex-1">
          <p class="font-medium">{{ t('WORKSPACE_V2.SCORING.LOAD_ERROR') }}</p>
          <p class="text-xs mt-1">{{ loadError }}</p>
          <button
            type="button"
            class="text-xs underline mt-1 hover:opacity-80"
            data-testid="scoring-retry"
            @click="loadConfig"
          >
            {{ t('WORKSPACE_V2.SCORING.RETRY') }}
          </button>
        </div>
      </div>

      <p
        v-if="loading && !loaded"
        role="status"
        class="py-8 text-sm text-n-slate-11"
      >
        {{ t('WORKSPACE_V2.SCORING.LOADING') }}
      </p>
      <template v-if="loaded">
        <!-- Toggle master + platform AI readiness -->
        <section
          class="border border-n-weak rounded-2xl p-5 bg-n-solid-1 space-y-4"
        >
          <header
            class="flex flex-col items-start justify-between gap-4 sm:flex-row"
          >
            <div class="flex-1">
              <h2 class="font-semibold text-n-slate-12">
                {{ t('WORKSPACE_V2.SCORING.ENABLE') }}
              </h2>
              <p class="text-xs text-n-slate-11 mt-1 leading-relaxed">
                {{ t('WORKSPACE_V2.SCORING.ENABLE_DESCRIPTION') }}
              </p>
            </div>
            <div class="inline-flex items-center gap-2 text-sm shrink-0">
              <Switch
                v-model="cfg.enabled"
                :aria-label="t('WORKSPACE_V2.SCORING.ENABLE')"
              />
              <span
                :class="
                  cfg.enabled
                    ? 'text-n-slate-12 font-medium'
                    : 'text-n-slate-11'
                "
              >
                {{
                  cfg.enabled
                    ? t('WORKSPACE_V2.SCORING.ACTIVE')
                    : t('WORKSPACE_V2.SCORING.INACTIVE')
                }}
              </span>
            </div>
          </header>

          <div
            class="rounded-xl border px-3 py-2 text-xs"
            :class="
              cfg.text_ai_ready
                ? 'border-n-teal-7 bg-n-teal-3 text-n-teal-11'
                : 'border-n-amber-7 bg-n-amber-3 text-n-amber-11'
            "
          >
            <p class="font-medium">
              {{
                cfg.text_ai_ready
                  ? t('WORKSPACE_V2.SCORING.AI_AVAILABLE')
                  : t('WORKSPACE_V2.SCORING.AI_UNAVAILABLE_STATUS')
              }}
            </p>
            <p class="mt-0.5 opacity-90">
              {{
                cfg.text_ai_ready
                  ? t('WORKSPACE_V2.SCORING.AI_READY_DESCRIPTION')
                  : t('WORKSPACE_V2.SCORING.AI_UNAVAILABLE_DESCRIPTION')
              }}
            </p>
          </div>
        </section>

        <!-- Agendamento -->
        <section
          class="border border-n-weak rounded-2xl p-5 bg-n-solid-1 space-y-4"
        >
          <h2 class="font-semibold text-n-slate-12">
            {{ t('WORKSPACE_V2.SCORING.SCHEDULE') }}
          </h2>

          <div v-if="!advancedMode" class="space-y-2">
            <label class="block text-sm font-medium text-n-slate-12">{{
              t('WORKSPACE_V2.SCORING.DAILY_AT')
            }}</label>
            <div class="flex flex-wrap items-center gap-2">
              <select
                v-model="simpleHour"
                :aria-label="t('WORKSPACE_V2.SCORING.HOUR')"
                class="rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 w-20"
              >
                <option v-for="h in HOUR_OPTIONS" :key="h" :value="h">
                  {{ h }}
                </option>
              </select>
              <span class="text-n-slate-12 font-medium">:</span>
              <select
                v-model="simpleMinute"
                :aria-label="t('WORKSPACE_V2.SCORING.MINUTE')"
                class="rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 w-20"
              >
                <option v-for="m in MINUTE_OPTIONS" :key="m" :value="m">
                  {{ m }}
                </option>
              </select>
              <span class="text-xs text-n-slate-11">{{
                t('WORKSPACE_V2.SCORING.SERVER_TIME')
              }}</span>
              <button
                type="button"
                class="ml-auto text-xs text-n-slate-11 hover:text-n-slate-12 underline"
                @click="advancedMode = true"
              >
                {{ t('WORKSPACE_V2.SCORING.ADVANCED_MODE') }}
              </button>
            </div>
            <p class="text-[11px] text-n-slate-11">
              {{ t('WORKSPACE_V2.SCORING.SCHEDULE_HINT') }}
            </p>
          </div>

          <div v-else class="space-y-2">
            <div class="flex items-center justify-between">
              <label
                for="scoring-cron"
                class="block text-sm font-medium text-n-slate-12"
                >{{ t('WORKSPACE_V2.SCORING.CRON_EXPRESSION') }}</label
              >
              <button
                type="button"
                class="text-xs text-n-slate-11 hover:text-n-slate-12 underline"
                @click="
                  advancedMode = false;
                  syncSimpleFromCron();
                "
              >
                {{ t('WORKSPACE_V2.SCORING.SIMPLE_MODE') }}
              </button>
            </div>
            <input
              id="scoring-cron"
              v-model="cfg.cron_expression"
              :placeholder="CRON_EXAMPLE"
              class="w-full font-mono rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12"
            />
            <p class="text-[11px] text-n-slate-11">
              {{ t('WORKSPACE_V2.SCORING.CRON_HINT') }}
              <a
                href="https://crontab.guru"
                target="_blank"
                rel="noopener noreferrer"
                class="underline"
                >{{ CRON_REFERENCE }}</a
              >
            </p>
          </div>

          <div class="grid md:grid-cols-2 gap-3">
            <div>
              <label
                for="scoring-limit"
                class="block text-sm font-medium text-n-slate-12 mb-1"
                >{{ t('WORKSPACE_V2.SCORING.RUN_LIMIT') }}</label
              >
              <input
                id="scoring-limit"
                v-model.number="cfg.max_per_run"
                type="number"
                min="1"
                max="5000"
                step="50"
                class="w-full rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12"
              />
              <p class="text-[11px] text-n-slate-11 mt-1">
                {{ t('WORKSPACE_V2.SCORING.RUN_LIMIT_HINT') }}
              </p>
            </div>
            <div class="flex items-end pb-4">
              <label
                class="inline-flex items-start gap-2 text-sm text-n-slate-12"
              >
                <input
                  v-model="cfg.changed_only"
                  type="checkbox"
                  class="rounded border-n-weak mt-0.5"
                />
                <span>
                  {{ t('WORKSPACE_V2.SCORING.CHANGED_ONLY') }}
                  <span
                    class="block text-[11px] text-n-slate-11 mt-0.5 font-normal"
                  >
                    {{ t('WORKSPACE_V2.SCORING.CHANGED_ONLY_HINT') }}
                  </span>
                </span>
              </label>
            </div>
          </div>

          <div
            class="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-n-slate-11 bg-n-alpha-1 rounded-xl px-3 py-2"
          >
            <span class="inline-flex items-center gap-1">
              <span class="i-ph-calendar size-3.5" />
              <strong class="text-n-slate-12">{{ nextRunDescription }}</strong>
            </span>
            <span
              v-for="line in lastRunLines"
              :key="line.kind"
              :data-testid="`scoring-last-${line.kind}`"
            >
              {{ line.text }}
            </span>
          </div>
        </section>

        <!-- Escopo de análise -->
        <section
          class="border border-n-weak rounded-2xl p-5 bg-n-solid-1 space-y-4"
        >
          <h2 class="font-semibold text-n-slate-12">
            {{ t('WORKSPACE_V2.SCORING.SCOPE') }}
          </h2>

          <!-- Caixas de entrada -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">{{
              t('WORKSPACE_V2.SCORING.INBOXES')
            }}</label>
            <p class="text-[11px] text-n-slate-11 mb-2">
              {{ t('WORKSPACE_V2.SCORING.INBOXES_HINT') }}
              <strong>{{ inboxSummary }}</strong>
              {{ t('WORKSPACE_V2.SCORING.INBOXES_ALL_HINT') }}
            </p>
            <div
              v-if="availableInboxes.length"
              class="grid sm:grid-cols-2 gap-2 border border-n-weak rounded-xl p-3 bg-n-alpha-1"
            >
              <label
                v-for="inbox in availableInboxes"
                :key="inbox.id"
                class="flex items-start gap-2 text-sm text-n-slate-12 cursor-pointer"
              >
                <input
                  type="checkbox"
                  :checked="cfg.inbox_ids.includes(Number(inbox.id))"
                  class="mt-0.5 rounded border-n-weak"
                  @change="toggleInbox(inbox.id)"
                />
                <span class="flex-1">
                  <span class="font-medium">{{ inbox.name }}</span>
                  <span class="block text-[10px] text-n-slate-11 uppercase">{{
                    translateChannelLabel(inbox, t)
                  }}</span>
                </span>
              </label>
            </div>
            <p v-else class="text-xs text-n-slate-11 italic">
              {{ t('WORKSPACE_V2.SCORING.NO_INBOXES') }}
            </p>
          </div>

          <!-- Ignorar grupos -->
          <label
            class="flex items-start gap-2 text-sm text-n-slate-12 cursor-pointer"
          >
            <input
              v-model="cfg.ignore_whatsapp_groups"
              type="checkbox"
              class="mt-0.5 rounded border-n-weak"
            />
            <span>
              {{ t('WORKSPACE_V2.SCORING.IGNORE_GROUPS') }}
              <span
                class="block text-[11px] text-n-slate-11 mt-0.5 font-normal"
              >
                {{ t('WORKSPACE_V2.SCORING.IGNORE_GROUPS_HINT') }}
              </span>
            </span>
          </label>

          <!-- Groups engagement context (always injected when membership exists) -->
          <div
            class="rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-3 space-y-1"
          >
            <div
              class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
            >
              <span class="i-ph-users size-4 text-n-slate-11" />
              {{ t('WORKSPACE_V2.SCORING.GROUPS_CONTEXT_TITLE') }}
            </div>
            <p class="text-[11px] text-n-slate-11 leading-relaxed">
              {{ t('WORKSPACE_V2.SCORING.GROUPS_CONTEXT_DESCRIPTION') }}
            </p>
            <p class="text-[11px] text-n-slate-11">
              {{ t('WORKSPACE_V2.SCORING.GROUPS_CONTEXT_NOTICE') }}
            </p>
          </div>
        </section>

        <!-- Prompt customizado -->
        <section
          class="border border-n-weak rounded-2xl p-5 bg-n-solid-1 space-y-3"
        >
          <header class="flex items-center justify-between gap-3 flex-wrap">
            <h2 id="scoring-prompt-label" class="font-semibold text-n-slate-12">
              {{ t('WORKSPACE_V2.SCORING.PROMPT_TITLE') }}
            </h2>
            <div class="flex items-center gap-3">
              <button
                type="button"
                class="text-xs text-n-slate-11 hover:text-n-slate-12 underline"
                data-testid="scoring-preview"
                @click="showPreview = true"
              >
                {{ t('WORKSPACE_V2.SCORING.VIEW_PROMPT') }}
              </button>
              <button
                type="button"
                class="text-xs text-n-slate-11 hover:text-n-slate-12 underline"
                @click="resetToDefaultPrompt"
              >
                {{ t('WORKSPACE_V2.SCORING.USE_DEFAULT_PROMPT') }}
              </button>
            </div>
          </header>
          <p class="text-xs text-n-slate-11">
            {{ t('WORKSPACE_V2.SCORING.PROMPT_DESCRIPTION') }}
          </p>
          <textarea
            ref="promptTextareaRef"
            v-model="cfg.custom_prompt"
            aria-labelledby="scoring-prompt-label"
            rows="10"
            class="w-full font-mono text-xs rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-n-slate-12"
            :placeholder="t('WORKSPACE_V2.SCORING.PROMPT_PLACEHOLDER')"
          />

          <!-- Chips de etiquetas e estágios disponíveis -->
          <div v-if="availableLabels.length" class="space-y-1">
            <p class="text-[11px] text-n-slate-11 font-medium uppercase">
              {{ t('WORKSPACE_V2.SCORING.LABELS_HINT') }}
            </p>
            <div class="flex flex-wrap gap-1.5">
              <button
                v-for="label in availableLabels"
                :key="'lbl-' + label"
                type="button"
                class="px-2 py-0.5 rounded-md bg-n-alpha-2 border border-n-weak text-xs text-n-slate-12 hover:border-n-brand hover:bg-n-brand/10 transition-colors"
                @click="insertIntoPrompt(`&quot;${label}&quot;`)"
              >
                {{ label }}
              </button>
            </div>
          </div>

          <div v-if="availableFunnels.length" class="space-y-1">
            <p class="text-[11px] text-n-slate-11 font-medium uppercase">
              {{ t('WORKSPACE_V2.SCORING.FUNNELS_HINT') }}
            </p>
            <p class="text-[11px] text-n-slate-11">
              {{ t('WORKSPACE_V2.SCORING.FUNNELS_DESCRIPTION') }}
            </p>
            <div class="space-y-1">
              <div
                v-for="funnel in availableFunnels"
                :key="'fn-' + funnel.id"
                class="flex flex-wrap gap-1.5 items-center"
              >
                <span class="text-[11px] text-n-slate-11 mr-1"
                  >{{ funnel.name }}:</span
                >
                <button
                  v-for="stage in funnel.stages"
                  :key="'st-' + funnel.id + '-' + stage"
                  type="button"
                  class="px-2 py-0.5 rounded-md bg-n-alpha-2 border border-n-weak text-xs text-n-slate-12 hover:border-n-brand hover:bg-n-brand/10 transition-colors"
                  :title="
                    t('WORKSPACE_V2.SCORING.INSERT_STAGE', {
                      funnel: funnel.name,
                      stage,
                    })
                  "
                  @click="
                    insertIntoPrompt(`&quot;${funnel.name} → ${stage}&quot;`)
                  "
                >
                  {{ stage }}
                </button>
              </div>
            </div>
          </div>

          <p
            v-if="!availableLabels.length && !availableFunnels.length"
            class="text-[11px] text-n-slate-11 italic"
          >
            {{ t('WORKSPACE_V2.SCORING.NO_PROMPT_REFERENCES') }}
          </p>
        </section>

        <!-- Modal: preview do prompt final -->
        <div
          v-if="showPreview && !isWorkspaceV2"
          class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          @click.self="showPreview = false"
        >
          <div
            class="bg-n-solid-1 border border-n-weak rounded-2xl shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col"
          >
            <header
              class="flex items-center justify-between px-5 py-3 border-b border-n-weak"
            >
              <h3 class="font-semibold text-n-slate-12">
                {{ t('WORKSPACE_V2.SCORING.PREVIEW_TITLE') }}
              </h3>
              <button
                type="button"
                class="text-n-slate-11 hover:text-n-slate-12"
                :aria-label="t('WORKSPACE_V2.SCORING.CLOSE')"
                @click="showPreview = false"
              >
                <span class="i-ph-x size-4" aria-hidden="true" />
              </button>
            </header>
            <div class="p-5 overflow-y-auto flex-1">
              <p class="text-xs text-n-slate-11 mb-3">
                {{ t('WORKSPACE_V2.SCORING.PREVIEW_DESCRIPTION') }}
              </p>
              <pre
                class="font-mono text-xs bg-n-alpha-1 rounded-xl p-4 whitespace-pre-wrap text-n-slate-12 border border-n-weak"
                >{{ previewSystemPrompt }}</pre
              >
            </div>
            <footer class="px-5 py-3 border-t border-n-weak flex justify-end">
              <button
                type="button"
                class="px-4 py-2 rounded-xl bg-n-brand text-white text-sm font-medium"
                @click="showPreview = false"
              >
                {{ t('WORKSPACE_V2.SCORING.CLOSE') }}
              </button>
            </footer>
          </div>
        </div>

        <Dialog
          v-if="isWorkspaceV2"
          ref="previewDialog"
          :title="t('WORKSPACE_V2.SCORING.PREVIEW_TITLE')"
          :description="t('WORKSPACE_V2.SCORING.PREVIEW_DESCRIPTION')"
          :show-confirm-button="false"
          :cancel-button-label="t('WORKSPACE_V2.SCORING.CLOSE')"
          @close="showPreview = false"
        >
          <pre
            class="whitespace-pre-wrap break-words rounded-xl border border-n-weak bg-n-alpha-1 p-4 text-sm text-n-slate-12"
            >{{ previewSystemPrompt }}</pre
          >
        </Dialog>

        <!-- Save + Run now -->
        <p class="text-sm text-n-slate-11">
          {{ t('WORKSPACE_V2.SCORING.RUN_SAVED_HINT') }}
        </p>
        <div class="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            class="px-4 py-2 rounded-xl bg-n-brand text-white text-sm font-medium hover:bg-n-brand/90 disabled:opacity-50"
            data-testid="scoring-save"
            :disabled="saving || loading || running"
            @click="save"
          >
            {{
              saving
                ? t('WORKSPACE_V2.SCORING.SAVING')
                : t('WORKSPACE_V2.SCORING.SAVE')
            }}
          </button>
          <button
            type="button"
            class="px-4 py-2 rounded-xl border border-n-brand text-n-brand text-sm font-medium hover:bg-n-brand/10 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="scoring-run"
            :disabled="running || saving || !cfg.text_ai_ready"
            :title="
              cfg.text_ai_ready
                ? t('WORKSPACE_V2.SCORING.RUN_NOW_TITLE')
                : t('WORKSPACE_V2.SCORING.AI_UNAVAILABLE')
            "
            @click="runNow"
          >
            <span class="i-ph-play size-4" />
            {{
              running
                ? t('WORKSPACE_V2.SCORING.RUNNING')
                : t('WORKSPACE_V2.SCORING.RUN_NOW')
            }}
          </button>
          <p
            v-if="flash"
            role="status"
            class="text-sm text-n-teal-11 inline-flex items-center gap-1"
          >
            <span class="i-ph-check size-4" />
            {{ t('WORKSPACE_V2.SCORING.SAVED') }}
          </p>
          <p
            v-if="saveError"
            role="alert"
            class="text-sm text-n-ruby-11 inline-flex items-center gap-1"
          >
            <span class="i-ph-warning size-4 shrink-0" />
            {{ saveError }}
          </p>
          <p
            v-if="runFlash"
            role="status"
            class="text-sm inline-flex items-center gap-1"
            :class="runFlashSuccess ? 'text-n-teal-11' : 'text-n-amber-11'"
          >
            <span
              class="size-4"
              :class="[runFlashSuccess ? 'i-ph-check-circle' : 'i-ph-info']"
            />
            {{ runFlash }}
          </p>
        </div>
      </template>
    </div>
  </div>
</template>
